'use client';

import React, { createContext, useContext, useCallback } from 'react';
import { toast } from 'sonner';

interface ToastContextValue {
  success: (message: string, description?: string) => void;
  error: (message: string, description?: string) => void;
  info: (message: string, description?: string) => void;
  warning: (message: string, description?: string) => void;
  loading: (message: string) => string | number;
  dismiss: (id?: string | number) => void;
  promise: <T>(
    promise: Promise<T>,
    messages: { loading: string; success: string; error: string }
  ) => Promise<T>;
}

const ToastContext = createContext<ToastContextValue>({
  success: () => {},
  error: () => {},
  info: () => {},
  warning: () => {},
  loading: () => '',
  dismiss: () => {},
  promise: async (p) => p,
});

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const success = useCallback((message: string, description?: string) => {
    toast.success(message, { description });
  }, []);

  const error = useCallback((message: string, description?: string) => {
    toast.error(message, { description });
  }, []);

  const info = useCallback((message: string, description?: string) => {
    toast.info(message, { description });
  }, []);

  const warning = useCallback((message: string, description?: string) => {
    toast.warning(message, { description });
  }, []);

  const loading = useCallback((message: string) => {
    return toast.loading(message);
  }, []);

  const dismiss = useCallback((id?: string | number) => {
    toast.dismiss(id);
  }, []);

  const promise = useCallback(
    async <T,>(
      p: Promise<T>,
      messages: { loading: string; success: string; error: string }
    ): Promise<T> => {
      toast.promise(p, messages);
      return p;
    },
    []
  );

  return (
    <ToastContext.Provider value={{ success, error, info, warning, loading, dismiss, promise }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
